import React, { useState, useEffect } from 'react';
import BookButton from './BookButton';
import { HashLink as Link } from 'react-router-hash-link';

function Coaching({ coaching, focus }) {
  const [highlightFocus, setHighlightFocus] = useState(null);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const updateHighlightFocus = debounce(() => {
    const hash = window.location.hash;
    if (hash === '#onlineCoachingInfo') {
      setHighlightFocus('online');
    } else if (hash === '#inPersonCoachingInfo') {
      setHighlightFocus('inPerson');
    } else {
      setHighlightFocus(null);
    }
  }, 0);

  useEffect(() => {
    updateHighlightFocus();
    window.addEventListener('hashchange', updateHighlightFocus);
    window.addEventListener('scroll', updateHighlightFocus);

    return () => {
      window.removeEventListener('hashchange', updateHighlightFocus);
      window.removeEventListener('scroll', updateHighlightFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseEnter = (section) => {
    setHighlightFocus(section);
  };

  const handleMouseLeave = () => {
    setHighlightFocus(null);
  };

  return (
    <div 
      id="coaching" 
      className="relative bg-cover bg-center py-16 lg:py-24"
      style={{ backgroundImage: `url(${coaching.img_url})` }}
    >
      {/* Semi-transparent dark overlay for the entire section background */}
      <div className="absolute inset-0 bg-black/60 z-0"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-between h-full">
        
        {/* Section Heading */}
        <div className="text-center mb-12">
          <h2 className="text-xl sm:text-2xl font-extrabold text-white mb-4">
            1-on-1 Personalized Coaching
          </h2>
        </div>

        {/* Home & Online Coaching Cards */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-stretch mb-12">
          
          {/* Left Side: Home Coaching */}
          <div 
            id="inPersonCoachingInfo"
            className={`flex-1 flex flex-col bg-[#162E3B]/90 backdrop-blur-md border-2 rounded-2xl p-8 lg:p-12 transition-all duration-300 ${
              highlightFocus === 'online' 
                ? 'opacity-40 scale-98 border-white/5' 
                : highlightFocus === 'inPerson'
                ? 'opacity-100 scale-102 border-[#FFD82D] shadow-2xl shadow-[#FFD82D]/15'
                : 'opacity-100 border-white/10 shadow-lg'
            }`}
            onMouseEnter={() => handleMouseEnter('inPerson')}
            onMouseLeave={handleMouseLeave}
          >
            <div className="flex-grow flex flex-col justify-between">
              <div>
                <span className="inline-block text-xs uppercase font-extrabold tracking-widest text-[#FFD82D] bg-[#FFD82D]/10 px-3 py-1 rounded-full mb-4 border border-[#FFD82D]/20">
                  Direct In-Person Support
                </span>
                <h3 className="text-lg font-bold text-white mb-5">
                  Home Coaching
                </h3>
                <p className="text-[#E5E7EB] text-base leading-relaxed mb-8">
                  Get personalized fitness coaching directly in your home. I bring the training equipment, safety guidelines, and motivational guidance right to your doorstep.
                </p>
              </div>
              
              <ul className="space-y-4 mb-6">
                {coaching.homeCoachingBulletPoints.map((item, index) => (
                  <li key={index} className="flex items-start text-base text-white font-medium">
                    <span className="flex-shrink-0 w-7 h-7 bg-[#FFD82D]/10 text-[#FFD82D] border border-[#FFD82D]/35 rounded-full flex items-center justify-center mr-3.5 mt-0.5">
                      ✓
                    </span>
                    <span className="mt-0.5">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Side: Online Coaching */}
          <div 
            id="onlineCoachingInfo"
            className={`flex-1 flex flex-col bg-[#162E3B]/90 backdrop-blur-md border-2 rounded-2xl p-8 lg:p-12 transition-all duration-300 ${
              highlightFocus === 'inPerson' 
                ? 'opacity-40 scale-98 border-white/5' 
                : highlightFocus === 'online'
                ? 'opacity-100 scale-102 border-[#FFD82D] shadow-2xl shadow-[#FFD82D]/15'
                : 'opacity-100 border-white/10 shadow-lg'
            }`}
            onMouseEnter={() => handleMouseEnter('online')}
            onMouseLeave={handleMouseLeave}
          >
            <div className="flex-grow flex flex-col justify-between">
              <div>
                <span className="inline-block text-xs uppercase font-extrabold tracking-widest text-[#FFD82D] bg-[#FFD82D]/10 px-3 py-1 rounded-full mb-4 border border-[#FFD82D]/20">
                  Flexible Remote Program
                </span>
                <h3 className="text-lg font-bold text-white mb-5">
                  Online Coaching
                </h3>
                <p className="text-[#E5E7EB] text-base leading-relaxed mb-8">
                  Experience full 1-on-1 online coaching with personal direct support throughout your journey. Your fitness plan is custom-tailored to your goals and schedule.
                </p>
              </div>

              <ul className="space-y-4 mb-6">
                {coaching.onlineCoachingBulletPoints.map((item, index) => (
                  <li key={index} className="flex items-start text-base text-white font-medium">
                    <span className="flex-shrink-0 w-7 h-7 bg-[#FFD82D]/10 text-[#FFD82D] border border-[#FFD82D]/35 rounded-full flex items-center justify-center mr-3.5 mt-0.5">
                      ✓
                    </span>
                    <span className="mt-0.5">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>

        {/* Action Call for Call */}
        <div className="bg-[#162E3B]/80 border border-white/10 backdrop-blur-sm rounded-2xl p-8 text-center max-w-3xl mx-auto w-full shadow-lg">
          <p className="text-base sm:text-lg text-white font-semibold mb-6">
            Apply for a <span className="text-[#FFD82D]">FREE call</span> first to see how we can work together.
          </p>
          <div className="flex justify-center">
            <Link to="/#contact" className="focus:outline-none inline-block">
              <BookButton text="Apply Now" />
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Coaching;
